import { useEffect, useId, useRef } from 'react';

const ensureMathJax = async () => {
  if (window.MathJax?.typesetPromise) return;

  window.MathJax = {
    tex: {
      inlineMath: [['$', '$'], ['\\(', '\\)']],
      displayMath: [['$$', '$$'], ['\\[', '\\]']],
      packages: ['base', 'ams'],
    },
    options: {
      ignoreHtmlClass: 'tex2jax_ignore',
      processHtmlClass: 'tex2jax_process',
    },
  };

  await new Promise((resolve, reject) => {
    const existing = document.getElementById('MathJax-script');
    if (existing) {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
      if (window.MathJax?.typesetPromise) resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = 'MathJax-script';
    script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-chtml.js';
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  // Po onload skryptu MathJax dokańcza inicjalizację asynchronicznie — dopiero
  // startup.promise gwarantuje, że typesetPromise/typeset są gotowe.
  try {
    await window.MathJax?.startup?.promise;
  } catch (_) {}
};

// JSXGraph woła MathJax.typeset() na treści etykiety, ale MathJax typesetuje tylko
// zawartość w delimiterach ($...$ / \(...\)). Generator zadań produkuje etykiety bez
// delimiterów (np. '\frac{2}{9}x'), więc MathJax je pomija i widać surowy LaTeX.
// Przechwytujemy board.create: dla text z useMathJax bez delimiterów doklejamy \( \).
const MATH_DELIM_RE = /\$|\\\(|\\\[/;
// Formatowanie etykiet w bazie jest niejednorodne: część ma useMathJax + LaTeX, część
// LaTeX BEZ flagi (renderuje surowe "\frac{7}{2}"), część unicode "√" bez flagi. Traktujemy
// jako matematykę wszystko, co zawiera backslash lub √ — niezależnie od flagi useMathJax.
const MATH_HINT_RE = /[\\√]/;

// Zwraca [nowyString, czyWymusicMathJax] dla etykiety text. Gdy wykryje matematykę:
// zamienia unicode √N na \sqrt{N}, wrappuje w \(...\) i sygnalizuje wymuszenie useMathJax
// (bez tej flagi JSXGraph nie odpali MathJax.typeset dla tej etykiety).
const prepareTextLabel = (str, explicitMathJax) => {
  if (!explicitMathJax && !MATH_HINT_RE.test(str)) return [str, false];
  let out = str.replace(/√\s*(\d+)/g, '\\sqrt{$1}');
  // Kolaps nadmiarowych backslashy z over-escapingu w danych (np. '7\\sqrt{3}' po new Function):
  // w trybie \(...\) MathJax traktuje \\ jako łamanie linii, więc \\sqrt gubi komendę.
  out = out.replace(/\\{2,}/g, '\\');
  if (!MATH_DELIM_RE.test(out)) out = '\\(' + out + '\\)';
  return [out, true];
};

// Ujednolicenie oznaczeń kątów niezależnie od tego, co wygenerował model:
//  - radius proporcjonalny -> 0.3 * min(dł. ramion). Model wpisywał radius w jednostkach
//                          wykresu (np. 2.0) na sztywno, przez co dla kątów rozwartych łuk
//                          sięgał poza trójkąt, a dla prostych "wyciągał się" do wierzchołka
//                          drugiego ramienia. Promień < każdego ramienia gwarantuje, że łuk
//                          kończy się na ramionach WEWNĄTRZ figury i siedzi blisko wierzchołka.
//  - orthoType sectordot-> kąt prosty oznaczony łukiem z kropką (zostawiamy — na maturze też
//                          uczeń mierzy ekierką; przypadkowe 90° na schemacie akceptujemy).
//  - offset etykiety     -> zdejmujemy ręczne przesunięcia i doklejamy mały offset wzdłuż
//                          dwusiecznej ciągnący etykietę do łuku. JSXGraph kładzie ją w
//                          odległości radius + ~fontsize, więc przy małym łuku zostaje luka.
const legVec = (p, q) => {
  if (!p || !q || typeof p.X !== 'function' || typeof q.X !== 'function') return null;
  return [p.X() - q.X(), p.Y() - q.Y()];
};

const normalizeAngleAttrs = (attributes, parents) => {
  const attrs = { ...(attributes || {}) };

  // Kąt: parents = [pkt1, wierzchołek, pkt2]. Ramiona idą od wierzchołka do pkt1 i pkt2.
  let radius = 'auto';
  let labelPull = null;
  if (Array.isArray(parents) && parents.length === 3) {
    const v1 = legVec(parents[0], parents[1]);
    const v2 = legVec(parents[2], parents[1]);
    if (v1 && v2) {
      const l1 = Math.hypot(v1[0], v1[1]);
      const l2 = Math.hypot(v2[0], v2[1]);
      if (l1 && l2) {
        // Promień TYLKO przycinamy, nigdy nie powiększamy: bierzemy min z tego, co podał
        // generator, oraz 0.32*min(ramię) (żeby łuk został wewnątrz figury). Dawny wzór
        // proporcjonalny powiększał małe łuki (np. radius 0.6 na trapezach z długimi
        // ramionami stawał się ~1) — wszystkie skargi były "za duży", nigdy "za mały".
        const dataR = typeof attributes?.radius === 'number' ? attributes.radius : Infinity;
        radius = Math.min(dataR, 0.32 * Math.min(l1, l2));
        // Dwusieczna = suma wektorów jednostkowych ramion (kierunek NA ZEWNĄTRZ od wierzchołka).
        // Offset zależny od szerokości kąta: wąskie kąty wypychamy na zewnątrz (dodatnie),
        // bo blisko wierzchołka etykieta nachodzi na ramiona; szerokie lekko dociągamy do
        // łuku (ujemne), jak wcześniej. out = 9/sin(kąt/2) - 17, clamp [-10, 30] px.
        const bx = v1[0] / l1 + v2[0] / l2;
        const by = v1[1] / l1 + v2[1] / l2;
        const bl = Math.hypot(bx, by);
        if (bl > 1e-6) {
          const cosT = Math.max(-1, Math.min(1, (v1[0] * v2[0] + v1[1] * v2[1]) / (l1 * l2)));
          const half = Math.acos(cosT) / 2;
          const out = Math.max(-10, Math.min(30, 9 / Math.sin(half) - 17));
          labelPull = [(bx / bl) * out, (by / bl) * out];
        }
      }
    }
  }
  attrs.radius = radius;
  attrs.orthoType = 'sectordot';
  const { offset, ...restLabel } = attrs.label || {};
  attrs.label = labelPull ? { ...restLabel, offset: labelPull } : restLabel;
  return attrs;
};

const patchBoardCreate = (board) => {
  if (!board || board.__matheloCreatePatched) return;
  board.__matheloCreatePatched = true;

  const originalCreate = board.create.bind(board);
  board.create = (type, parents, attributes) => {
    const kind = typeof type === 'string' ? type.toLowerCase() : '';

    if (
      kind === 'text' &&
      Array.isArray(parents) &&
      parents.length > 0 &&
      typeof parents[parents.length - 1] === 'string'
    ) {
      const attrs = attributes || {};
      const useMJ = attrs.useMathJax ?? board.options?.text?.useMathJax;
      const idx = parents.length - 1;
      const [newStr, forceMJ] = prepareTextLabel(parents[idx], useMJ);
      if (forceMJ) {
        parents = parents.slice();
        parents[idx] = newStr;
        // Wymuszamy useMathJax, bo bez niej JSXGraph nie odpali MathJax.typeset dla etykiet,
        // które w bazie miały LaTeX/√ bez tej flagi.
        attributes = { ...attrs, useMathJax: true };
      }
    } else if (kind === 'angle') {
      // JSXGraph rysuje kąt od parents[0] do parents[2] przeciwnie do wskazówek zegara.
      // Jeśli ta kolejność daje kąt WKLĘSŁY (>180°, ujemny iloczyn wektorowy ramion),
      // zamieniamy ramiona — w szkolnej geometrii zawsze chcemy kąt wypukły (wewnętrzny).
      if (Array.isArray(parents) && parents.length === 3) {
        const v1 = legVec(parents[0], parents[1]);
        const v2 = legVec(parents[2], parents[1]);
        if (v1 && v2 && v1[0] * v2[1] - v1[1] * v2[0] < 0) {
          parents = [parents[2], parents[1], parents[0]];
        }
      }
      attributes = normalizeAngleAttrs(attributes, parents);
    } else if (kind === 'polygon' && Array.isArray(parents) && parents.length === 4) {
      // Generator rysuje kąt prosty ręcznie jako mały KWADRAT (4-pkt polygon) + osobna kropka.
      // Konwencja PL to łuk z kropką. Zamieniamy kwadrat na kąt z sectordot (łuk + kropka),
      // a ręczną kropkę wygaszamy niżej (gałąź 'point'). Wykrycie znacznika: krótkie (<0.8)
      // i ~prostopadłe boki od wierzchołka [0] (trapez/trójkąt mają boki rzędu jednostek).
      const v = parents[0], p1 = parents[1], p3 = parents[3];
      const l1 = legVec(p1, v), l3 = legVec(p3, v);
      if (l1 && l3) {
        const len1 = Math.hypot(l1[0], l1[1]);
        const len3 = Math.hypot(l3[0], l3[1]);
        const cosT = (l1[0] * l3[0] + l1[1] * l3[1]) / (len1 * len3 || 1);
        if (len1 > 0 && len1 < 0.8 && len3 > 0 && len3 < 0.8 && Math.abs(cosT) < 0.3) {
          // Kąt wypukły: przy ujemnym iloczynie wektorowym zamieniamy końce.
          let a = p1, b = p3;
          if (l1[0] * l3[1] - l1[1] * l3[0] < 0) { a = p3; b = p1; }
          const stroke = attributes?.borders?.strokeColor || attributes?.strokeColor || 'black';
          return originalCreate('angle', [a, v, b], {
            radius: len1, orthoType: 'sectordot', fillColor: 'none', strokeColor: stroke,
            strokeWidth: 1, fixed: true, highlight: false, withLabel: false, name: '',
          });
        }
      }
    } else if (kind === 'point') {
      // Ręczna kropka znacznika kąta prostego (mała, czarna, bez nazwy) — wygaszamy, bo
      // kropkę zapewnia teraz sectordot na kącie. Bez tego pojawiały się DWIE kropki.
      const nm = attributes?.name;
      const sz = attributes?.size;
      const isBlack = [attributes?.fillColor, attributes?.strokeColor, attributes?.color].includes('black');
      if ((nm === '' || nm == null) && typeof sz === 'number' && sz > 0 && sz <= 1.5 && isBlack) {
        attributes = { ...attributes, visible: false };
      }
    }

    return originalCreate(type, parents, attributes);
  };
};

// Naprawa kodu JSXGraph PRZED new Function. Generator wstawia stopień jako '45^\circ' —
// pojedynczy backslash (\c) to wadliwa sekwencja ucieczki JS, więc silnik JS wyrzuca
// backslash przy kompilacji (\circ -> circ) zanim nasz interceptor cokolwiek zobaczy.
// Tu `code` ma jeszcze prawdziwy backslash, więc zamieniamy wzorzec stopnia na unicode °,
// co wykonuje się i renderuje jako czysty stopień (bez MathJax).
const preprocessDiagramCode = (code) =>
  code.replace(/\^\s*\{?\s*\\circ\s*\}?/g, '°');

const fitAndResize = (board, divEl) => {
  board.zoomFit(1.1);
  const bb = board.getBoundingBox();
  const coordW = Math.abs(bb[2] - bb[0]);
  const coordH = Math.abs(bb[1] - bb[3]);

  if (coordW > 0 && coordH > 0 && divEl) {
    const pixelW = divEl.offsetWidth || 360;
    const pixelH = pixelW;
    divEl.style.height = pixelH + 'px';
    board.resizeContainer(pixelW, pixelH);
    board.zoomFit(1.1);
  }
};

const DiagramRenderer = ({ code }) => {
  const id = 'jsxg-' + useId().replace(/:/g, '');
  const boardRef = useRef(null);
  const divRef = useRef(null);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;

    const run = async () => {
      let JXG;
      try {
        JXG = require('jsxgraph');
      } catch (e) {
        console.error('JSXGraph nie jest zainstalowany:', e);
        return;
      }

      await ensureMathJax();

      if (cancelled) return;

      if (boardRef.current) {
        try {
          JXG.JSXGraph.freeBoard(boardRef.current);
        } catch (_) {}
      }

      try {
        const execCode = preprocessDiagramCode(code);
        const codeCallsInitBoard = /initBoard\s*\(/.test(execCode);
        let board;

        if (codeCallsInitBoard) {
          const patchedJXG = {
            ...JXG,
            JSXGraph: {
              ...JXG.JSXGraph,
              initBoard: (_anyId, opts) => {
                const b = JXG.JSXGraph.initBoard(id, {
                  showNavigation: false,
                  showCopyright: false,
                  pan: { enabled: false },
                  zoom: { enabled: false },
                  keepaspectratio: true,
                  ...opts,
                  // Po spreadzie opts, by kod diagramu nie mógł włączyć zdarzeń z powrotem.
                  // Wyłącza hover-highlight i zaznaczanie: JSXGraph robi hit-testing na
                  // globalnych listenerach, więc samo pointer-events:none nie wystarcza.
                  registerEvents: false,
                });
                boardRef.current = b;
                patchBoardCreate(b);
                return b;
              },
            },
          };

          new Function('board', 'JXG', execCode)(undefined, patchedJXG);
          board = boardRef.current;
        } else {
          board = JXG.JSXGraph.initBoard(id, {
            boundingbox: [-1, 10, 10, -1],
            axis: false,
            grid: false,
            showNavigation: false,
            showCopyright: false,
            pan: { enabled: false },
            zoom: { enabled: false },
            keepaspectratio: true,
            registerEvents: false,
          });
          boardRef.current = board;
          patchBoardCreate(board);
          new Function('board', 'JXG', execCode)(board, JXG);
        }

        if (!board || cancelled) return;

        fitAndResize(board, divRef.current);

        await window.MathJax?.typesetPromise?.([divRef.current]);
      } catch (e) {
        console.error('Błąd renderowania diagramu JSXGraph:', e);
      }
    };

    run();

    return () => {
      cancelled = true;
      if (boardRef.current) {
        try {
          const JXG = require('jsxgraph');
          JXG.JSXGraph.freeBoard(boardRef.current);
        } catch (_) {}
        boardRef.current = null;
      }
    };
  }, [code, id]);

  return (
    <div
      ref={divRef}
      id={id}
      className="tex2jax_process"
      style={{
        width: '100%',
        maxWidth: '420px',
        height: '420px',
        margin: '12px 0',
        userSelect: 'none',
        // Rysunki są czysto statyczne: blokujemy hover-highlight, zaznaczanie i klikanie
        // elementów JSXGraph (fixed:true blokuje tylko przeciąganie, nie podświetlanie).
        pointerEvents: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
    />
  );
};

export default DiagramRenderer;